import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ClipboardEvent, KeyboardEvent, ReactNode } from "react";
import {
  Baseline,
  Bold,
  Code,
  Italic,
  Link2,
  List,
  Smile,
  SquareCode,
  Strikethrough,
} from "lucide-react";
import { cn } from "@/utils/cn";
import {
  codeBlockText,
  editorHtmlToMarkdown,
  inlineMarkdownToHtml,
  markdownToEditorHtml,
  normalizeUrl,
} from "@/utils/messageMarkdown";

/**
 * The message composer's text area — a Gmail-chat-style rich input.
 *
 * Replaces the plain <textarea> the composers used, which could only ever show
 * the characters typed into it. This is a contenteditable surface with three
 * always-visible actions at the trailing edge of the text — the "A" formatting
 * toggle, emoji, and whatever the caller passes as `trailingActions` (the
 * composer's attach button). The "A" reveals the formatting bar above the
 * text: bold, italic, strikethrough, bulleted list, link, inline code and code
 * block. Collapsed by default, like Gmail's chat composer, since most messages
 * carry no formatting and the bar would otherwise be permanent furniture.
 *
 * The `value` contract is unchanged from the textarea it replaces: a plain
 * string, in and out. Formatting is serialised to the lightweight markdown in
 * utils/messageMarkdown, so the send endpoints, the message tables and every
 * caller keep working on text, and MessageBody renders the marks back out in
 * the bubble.
 *
 * Two implementation notes worth knowing before editing this:
 *
 * 1. The editor is NOT re-rendered from `value` while it is being typed into.
 *    Seeding innerHTML on every keystroke would reset the caret to the start of
 *    the node on each character. `lastEmittedRef` holds the markdown we last
 *    handed to `onChange`, and the sync effect only re-seeds when `value`
 *    arrives as something we did not emit — i.e. a real external change, like
 *    the composer clearing the field after a send.
 *
 * 2. Formatting uses document.execCommand. It is deprecated but is still the
 *    only built-in way to toggle marks across a selection, is supported
 *    everywhere we ship, and needs no editor dependency. The commands it can't
 *    do — code spans and code blocks — are done with the Range API below.
 */

/**
 * Marks the portalled emoji/link panels.
 *
 * They live on <body>, outside whatever dialog hosts the input, so a Radix
 * DismissableLayer counts a click on an emoji as an outside click and closes
 * the dialog under it. Any dialog that hosts a RichMessageInput should ignore
 * interactions matching this selector — see ComposerModalShell.
 */
export const RICH_MSG_POPOVER_SELECTOR = "[data-rich-msg-popover]";

export interface RichMessageInputProps {
  /** Message markdown. Same plain-string contract as a textarea's value. */
  value: string;
  onChange: (value: string) => void;
  /** Enter (without Shift). The parent decides whether a send is allowed. */
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Rendered after the emoji button in the input's action row — the
   *  composer's attach button. Style it with the `rich-msg-tool` class so it
   *  matches the two beside it. */
  trailingActions?: ReactNode;
  /** Wrapper class, for sizing within the composer row. */
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  ariaLabel?: string;
  /** Take focus on mount — the direct-message drawers open onto the composer. */
  autoFocus?: boolean;
  /**
   * Start with the formatting bar open instead of collapsed behind the "A".
   *
   * Chat defaults to collapsed: a drawer is narrow and most messages are a
   * sentence. A post composer is the opposite — it is a full modal and the
   * formatting is the point — so it opts in rather than making everyone who
   * writes a post find the toggle first.
   */
  defaultFormattingOpen?: boolean;
  /**
   * A form field rather than a composer: Enter starts a new line (or the next
   * bullet) instead of sending. Pair it with no `onSubmit`.
   */
  multiline?: boolean;
  /**
   * Where the emoji/link panels are portalled. Defaults to <body>.
   *
   * INSIDE A RADIX MODAL DIALOG, PASS THE DIALOG'S CONTENT ELEMENT.
   * --------------------------------------------------------------
   * A modal Dialog wraps its content in a focus scope that traps focus: any
   * `focusin` on an element outside that subtree is undone and focus is pulled
   * straight back. A panel portalled to <body> is outside it, so its inputs
   * cannot hold focus — the Insert link panel opened, looked fine, and could
   * not be typed into at all. (The emoji panel survived because clicking never
   * needs focus, which is why only the link one appeared broken.)
   *
   * Portalling into the dialog's own content element puts the panel inside the
   * focus scope, so it keeps focus. It still escapes the scrolling body's
   * overflow — `position: fixed` is not clipped by an ancestor's `overflow`
   * unless that ancestor is its containing block, which only happens when the
   * ancestor is transformed. ComposerModalShell deliberately centres without a
   * transform for exactly this reason; do not put one back.
   */
  popoverContainer?: HTMLElement | null;
}

interface ActiveMarks {
  bold: boolean;
  italic: boolean;
  strike: boolean;
  list: boolean;
  code: boolean;
  codeBlock: boolean;
}

const NO_MARKS: ActiveMarks = {
  bold: false,
  italic: false,
  strike: false,
  list: false,
  code: false,
  codeBlock: false,
};

/** Caret anchor for an inline-code span the user hasn't typed into yet. */
const ZERO_WIDTH = "\u200B";
const ZERO_WIDTH_RE = /\u200B/g;

/**
 * A curated set rather than a full emoji index: a chat composer needs the
 * ones people actually reach for, and a complete picker would mean either a
 * dependency or a data file larger than this component.
 *
 * Space-separated so the rows stay readable — an array literal of this many
 * items gets reformatted to one emoji per line.
 */
export const EMOJI_GROUPS: { label: string; emoji: string }[] = [
  {
    label: "Smileys",
    // prettier-ignore
    emoji: "😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇 🥰 😍 😘 😚 🥲 😋 😛 😜 🤪 🤗 🤭 🤔 😐 😑 😶 😏 😒 🙄 😬 😴 🤤 😵 🤯 🥳 🥹 😢 😭 😤 😠 😡 😰 😱 🥵 🥶 😳",
  },
  {
    label: "Gestures & people",
    // prettier-ignore
    emoji: "👍 👎 👌 ✌️ 🤞 🤟 👏 🙌 👐 🤝 🙏 💪 🫶 👋 🤙 ☝️ 👆 👇 👉 👈 ✍️ 🫡 🙋 🤷 🤦 💁 🧑‍💻 👩‍💻 👨‍💻 🧠 👀 🎉",
  },
  {
    label: "Hearts & symbols",
    // prettier-ignore
    emoji: "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 💖 💯 ✅ ☑️ ❌ ⚠️ ❗ ❓ 💡 ⭐ 🌟 ✨ 🔥 ⚡ 🚀 🏆 🎯 📈 📉 🔒 🔑 ♻️ ➕ ➖",
  },
  {
    label: "Work & objects",
    // prettier-ignore
    emoji: "📌 📎 📝 📄 📊 📅 🗓️ ⏰ ⏳ 📣 🔔 💬 📩 📬 ☕ 🍕 🎂 🎁 💰 💳 🏁 🛠️ ⚙️ 🧩 🔍 📱 💻 🖥️ 🗂️ 🔗 📚 🥂",
  },
];

/** Nearest ancestor of `node` with one of `tags`, stopping at the editor. */
function closestWithin(root: HTMLElement, node: Node | null, tags: string[]): HTMLElement | null {
  let current: Node | null = node;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE && tags.includes((current as HTMLElement).tagName)) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return null;
}

/** queryCommandState throws on some hosts (and in jsdom); never let it break a render. */
function commandState(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

const ToolbarButton = ({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    /* Keeps the click from blurring the editor: without it the selection the
       command is about to act on is gone by the time onClick runs. */
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={active}
    title={label}
    className={cn("rich-msg-tool", active && "is-active")}
  >
    {children}
  </button>
);

/**
 * Places a portalled popover next to its anchor, in viewport coordinates.
 *
 * Returns a `fixed` style: aligned to the anchor's near edge (`align`),
 * preferring to sit ABOVE it and flipping below only when there is not enough
 * room. Both axes are clamped to the viewport so the panel can never be half
 * off-screen on a short window or a phone.
 *
 * `useLayoutEffect` rather than `useEffect`: the popover must be measured and
 * positioned before the browser paints, or it flashes at 0,0 on every open.
 */
function usePopoverPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  popoverRef: React.RefObject<HTMLElement | null>,
  open: string | null,
  align: "start" | "end" = "end",
): React.CSSProperties {
  const [style, setStyle] = useState<React.CSSProperties>({
    // Hidden until measured — see above.
    visibility: "hidden",
    top: 0,
    left: 0,
  });

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;

      const margin = 8;
      const a = anchor.getBoundingClientRect();
      const { offsetWidth: width, offsetHeight: height } = popover;

      const above = a.top - height - margin;
      const below = a.bottom + margin;
      const top = above >= margin ? above : Math.min(below, window.innerHeight - height - margin);

      const preferred = align === "start" ? a.left : a.right - width;
      const left = Math.min(Math.max(margin, preferred), window.innerWidth - width - margin);

      setStyle({ visibility: "visible", top: Math.max(margin, top), left: Math.max(margin, left) });
    };

    place();

    // Follow the anchor if anything moves. `true` captures scrolls on ANY
    // ancestor, which is the case that matters — the composer's modal body
    // scrolls, and the window does not.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align, anchorRef, popoverRef]);

  // Reset so the next open measures again rather than flashing at the last
  // position, which may be nowhere near the anchor by then.
  useEffect(() => {
    if (!open) setStyle({ visibility: "hidden", top: 0, left: 0 });
  }, [open]);

  return style;
}

export const RichMessageInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Type a message...",
  disabled = false,
  trailingActions,
  className,
  /** One line of text plus the editor's padding — the collapsed resting size. */
  minHeight = 48,
  maxHeight = 180,
  ariaLabel = "Message",
  autoFocus = false,
  defaultFormattingOpen = false,
  multiline = false,
  popoverContainer,
}: RichMessageInputProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  /** What we last handed to onChange — see note 1 in the file comment. */
  const lastEmittedRef = useRef("");
  const savedRangeRef = useRef<Range | null>(null);

  const [active, setActive] = useState<ActiveMarks>(NO_MARKS);
  const [openPopover, setOpenPopover] = useState<"emoji" | "link" | null>(null);
  const [linkDraft, setLinkDraft] = useState({ text: "", url: "" });
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showFormatting, setShowFormatting] = useState(defaultFormattingOpen);

  // ── Selection helpers ──────────────────────────────────────────────────────

  const saveSelection = useCallback(() => {
    const el = editorRef.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }, []);

  /** Puts the caret back where it was before focus went to a popover input. */
  const restoreSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return null;
    el.focus();

    const selection = window.getSelection();
    if (!selection) return null;

    const saved = savedRangeRef.current;
    const range = saved && el.contains(saved.commonAncestorContainer) ? saved.cloneRange() : null;

    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
      return range;
    }

    // No usable saved range (first interaction, or the node it pointed at is
    // gone) — fall back to the end of the content.
    const end = document.createRange();
    end.selectNodeContents(el);
    end.collapse(false);
    selection.removeAllRanges();
    selection.addRange(end);
    return end;
  }, []);

  const currentRange = useCallback((): Range | null => {
    const el = editorRef.current;
    const selection = window.getSelection();
    if (!el || !selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    return el.contains(range.commonAncestorContainer) ? range : null;
  }, []);

  const placeCaret = useCallback((range: Range) => {
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
    savedRangeRef.current = range.cloneRange();
  }, []);

  // ── Value in / out ─────────────────────────────────────────────────────────

  const refreshState = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    const text = (el.textContent ?? "").replace(ZERO_WIDTH_RE, "");
    setIsEmpty(!text.trim() && !el.querySelector("pre"));

    const focused = el === document.activeElement || el.contains(document.activeElement);
    if (!focused) {
      setActive(NO_MARKS);
      return;
    }

    const selection = window.getSelection();
    const anchor = selection && selection.rangeCount > 0 ? selection.anchorNode : null;
    const within = !!anchor && el.contains(anchor);
    const inCodeBlock = within && !!closestWithin(el, anchor, ["PRE"]);

    setActive({
      bold: commandState("bold"),
      italic: commandState("italic"),
      strike: commandState("strikeThrough"),
      list: commandState("insertUnorderedList"),
      code: within && !inCodeBlock && !!closestWithin(el, anchor, ["CODE"]),
      codeBlock: inCodeBlock,
    });
  }, []);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const markdown = editorHtmlToMarkdown(el.innerHTML);
    if (markdown === lastEmittedRef.current) return;
    lastEmittedRef.current = markdown;
    onChange(markdown);
  }, [onChange]);

  const commit = useCallback(() => {
    emit();
    refreshState();
  }, [emit, refreshState]);

  // Re-seed only on an external change — see note 1 in the file comment.
  useEffect(() => {
    const el = editorRef.current;
    if (!el || value === lastEmittedRef.current) return;
    el.innerHTML = markdownToEditorHtml(value);
    lastEmittedRef.current = value;
    savedRangeRef.current = null;
    refreshState();
  }, [value, refreshState]);

  // Deferred by a tick: the drawers that ask for focus mount this inside a
  // panel that is still animating in, and focusing mid-transition scrolls the
  // drawer's content.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const timer = window.setTimeout(() => editorRef.current?.focus(), 100);
    return () => window.clearTimeout(timer);
  }, [autoFocus, disabled]);

  // Toolbar highlighting follows the caret.
  useEffect(() => {
    const onSelectionChange = () => {
      const el = editorRef.current;
      if (!el) return;
      if (el === document.activeElement || el.contains(document.activeElement)) {
        saveSelection();
        refreshState();
      }
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [refreshState, saveSelection]);

  // ── Popovers ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!openPopover) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        toolbarRef.current?.contains(target) ||
        actionsRef.current?.contains(target)
      ) {
        return;
      }
      setOpenPopover(null);
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpenPopover(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openPopover]);

  // ── Commands ───────────────────────────────────────────────────────────────

  const runCommand = useCallback(
    (command: string) => {
      restoreSelection();
      document.execCommand(command);
      saveSelection();
      commit();
    },
    [commit, restoreSelection, saveSelection],
  );

  const unwrapInlineCode = useCallback(
    (code: HTMLElement) => {
      const parent = code.parentNode;
      if (!parent) return;
      const text = document.createTextNode((code.textContent ?? "").replace(ZERO_WIDTH_RE, ""));
      parent.replaceChild(text, code);
      const caret = document.createRange();
      caret.setStart(text, text.length);
      caret.collapse(true);
      placeCaret(caret);
    },
    [placeCaret],
  );

  const toggleInlineCode = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    restoreSelection();
    const range = currentRange();
    if (!range) return;

    const existing = closestWithin(el, range.startContainer, ["CODE"]);
    if (existing && !closestWithin(el, existing, ["PRE"])) {
      unwrapInlineCode(existing);
      commit();
      return;
    }

    const code = document.createElement("code");
    if (range.collapsed) {
      // Nothing selected: open an empty span and drop the caret inside it, so
      // what gets typed next is code. The zero-width anchor is what makes an
      // empty inline element focusable at all; it is stripped on serialise.
      code.textContent = ZERO_WIDTH;
      range.insertNode(code);
      const caret = document.createRange();
      caret.setStart(code.firstChild as Node, 1);
      caret.collapse(true);
      placeCaret(caret);
    } else {
      // Flattened to text on purpose — a code span holding bold or a link
      // would serialise to markers inside backticks, which read as literal.
      const selected = range.toString();
      range.deleteContents();
      code.textContent = selected;
      range.insertNode(code);
      const caret = document.createRange();
      caret.setStartAfter(code);
      caret.collapse(true);
      placeCaret(caret);
    }
    commit();
  }, [commit, currentRange, placeCaret, restoreSelection, unwrapInlineCode]);

  const unwrapCodeBlock = useCallback(
    (pre: HTMLElement) => {
      const parent = pre.parentNode;
      if (!parent) return;

      const fragment = document.createDocumentFragment();
      let last: HTMLElement | null = null;
      for (const line of codeBlockText(pre).split("\n")) {
        const block = document.createElement("div");
        if (line) block.textContent = line;
        else block.appendChild(document.createElement("br"));
        fragment.appendChild(block);
        last = block;
      }
      parent.replaceChild(fragment, pre);

      if (last) {
        const caret = document.createRange();
        caret.selectNodeContents(last);
        caret.collapse(false);
        placeCaret(caret);
      }
    },
    [placeCaret],
  );

  const toggleCodeBlock = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;

    restoreSelection();
    const range = currentRange();
    if (!range) return;

    const existing = closestWithin(el, range.startContainer, ["PRE"]);
    if (existing) {
      unwrapCodeBlock(existing);
      commit();
      return;
    }

    const selected = range.toString();
    range.deleteContents();

    const pre = document.createElement("pre");
    if (selected) pre.appendChild(document.createTextNode(selected));
    else pre.appendChild(document.createElement("br"));
    range.insertNode(pre);

    // Somewhere to type after the block: without a following sibling the caret
    // has no way out of a code block that ends the message.
    if (!pre.nextSibling) {
      const tail = document.createElement("div");
      tail.appendChild(document.createElement("br"));
      pre.parentNode?.insertBefore(tail, pre.nextSibling);
    }

    const caret = document.createRange();
    if (selected) {
      caret.selectNodeContents(pre);
      caret.collapse(false);
    } else {
      caret.setStart(pre, 0);
      caret.collapse(true);
    }
    placeCaret(caret);
    commit();
  }, [commit, currentRange, placeCaret, restoreSelection, unwrapCodeBlock]);

  const insertEmoji = useCallback(
    (emoji: string) => {
      restoreSelection();
      document.execCommand("insertText", false, emoji);
      saveSelection();
      commit();
    },
    [commit, restoreSelection, saveSelection],
  );

  const openLinkPopover = useCallback(() => {
    if (openPopover === "link") {
      setOpenPopover(null);
      return;
    }
    saveSelection();
    setLinkDraft({ text: savedRangeRef.current?.toString().trim() ?? "", url: "" });
    setLinkError(null);
    setOpenPopover("link");
  }, [openPopover, saveSelection]);

  const applyLink = useCallback(() => {
    const href = normalizeUrl(linkDraft.url);
    if (!href) {
      setLinkError("Enter a valid link, like https://example.com");
      return;
    }

    restoreSelection();
    const range = currentRange();
    if (!range) return;
    range.deleteContents();

    const anchor = document.createElement("a");
    anchor.setAttribute("href", href);
    anchor.textContent = linkDraft.text.trim() || href;
    range.insertNode(anchor);

    // A trailing space so continuing to type doesn't extend the link.
    const spacer = document.createTextNode(" ");
    anchor.parentNode?.insertBefore(spacer, anchor.nextSibling);

    const caret = document.createRange();
    caret.setStart(spacer, 1);
    caret.collapse(true);
    placeCaret(caret);

    setOpenPopover(null);
    setLinkDraft({ text: "", url: "" });
    setLinkError(null);
    commit();
  }, [commit, currentRange, linkDraft, placeCaret, restoreSelection]);

  // ── Typing ─────────────────────────────────────────────────────────────────

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && openPopover) {
      setOpenPopover(null);
      return;
    }

    if ((event.metaKey || event.ctrlKey) && !event.altKey) {
      const key = event.key.toLowerCase();
      if (key === "b") {
        event.preventDefault();
        runCommand("bold");
        return;
      }
      if (key === "i") {
        event.preventDefault();
        runCommand("italic");
        return;
      }
      if (key === "x" && event.shiftKey) {
        event.preventDefault();
        runCommand("strikeThrough");
        return;
      }
    }

    if (event.key !== "Enter") return;

    const el = editorRef.current;
    const selection = window.getSelection();
    const anchor = selection && selection.rangeCount > 0 ? selection.anchorNode : null;
    const inCodeBlock = !!el && !!anchor && !!closestWithin(el, anchor, ["PRE"]);

    // A code block is multi-line by nature, so Enter goes to a new line there
    // rather than sending. Everywhere else Enter sends, as the composer says.
    if (inCodeBlock) {
      if (event.shiftKey) return;
      event.preventDefault();
      document.execCommand("insertText", false, "\n");
      commit();
      return;
    }

    // A form field: Enter is the browser's own new line / next bullet.
    if (multiline) return;

    if (commandState("insertUnorderedList")) {
      event.preventDefault();
      // Shift+Enter continues the list; a second Shift+Enter on an empty
      // bullet ends it, which is the browser's own list behaviour.
      if (event.shiftKey) {
        document.execCommand("insertParagraph");
        commit();
      } else {
        onSubmit?.();
      }
      return;
    }

    if (event.shiftKey) return; // browser inserts the line break
    event.preventDefault();
    onSubmit?.();
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const el = editorRef.current;
    if (!el) return;

    const html = event.clipboardData.getData("text/html");
    const plain = event.clipboardData.getData("text/plain");
    event.preventDefault();

    const selection = window.getSelection();
    const anchor = selection && selection.rangeCount > 0 ? selection.anchorNode : null;
    if (anchor && closestWithin(el, anchor, ["PRE"])) {
      // Inside a code block everything pasted is literal.
      document.execCommand("insertText", false, plain);
      commit();
      return;
    }

    // Pasted markup is round-tripped through message markdown rather than
    // inserted as-is: only the formatting this editor can actually send
    // survives, and no foreign markup (styles, tables, scripts) gets in.
    const markdown = html ? editorHtmlToMarkdown(html) : plain;
    if (!markdown) return;

    // A one-line paste goes in inline, so it lands in the line being typed
    // rather than breaking it in two. Anything block-shaped (a list item, a
    // fence, several lines) needs the block markup.
    const isSingleLine =
      !/\n/.test(markdown) && !markdown.includes("```") && !/^\s*[-*]\s+/.test(markdown);
    document.execCommand(
      "insertHTML",
      false,
      isSingleLine ? inlineMarkdownToHtml(markdown) : markdownToEditorHtml(markdown),
    );
    saveSelection();
    commit();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Popovers are PORTALLED to <body> and positioned in viewport coordinates.
  //
  // They used to be `absolute bottom-full` inside the input, which is fine in a
  // message drawer but breaks anywhere the input sits inside a scroll container:
  // the post composer's modal body is `overflow-y-auto`, so an element opening
  // upward out of the input was clipped to the container's top edge and only its
  // bottom row of emoji stayed visible. No amount of z-index fixes that — an
  // ancestor's overflow always wins over a descendant's stacking.
  //
  // A portal escapes every ancestor's overflow. The cost is that position has to
  // be computed rather than inherited, which `popoverStyle` does below.
  // `data-rich-msg-popover` is on both panels so a Radix dismissable layer
  // (the post composer's Dialog) can recognise a click in here as its own
  // rather than as an outside click that should close the whole modal —
  // see RICH_MSG_POPOVER_SELECTOR.
  //
  // `pointer-events-auto` is NOT decoration. A Radix modal dialog puts
  // `pointer-events: none` on <body> and re-enables only its own subtree, so a
  // panel portalled to <body> renders perfectly and ignores every click — the
  // emoji picker inside the post composer was visible and completely inert.
  // Re-enabling it here is what makes a portalled popover usable inside any
  // modal, and is harmless outside one.
  const popoverBase = "fixed z-[60] pointer-events-auto rounded-[12px] p-3 rich-msg-popover";
  const emojiStyle = usePopoverPosition(
    actionsRef,
    popoverRef,
    openPopover === "emoji" ? "emoji" : null,
  );
  const linkStyle = usePopoverPosition(
    toolbarRef,
    popoverRef,
    openPopover === "link" ? "link" : null,
    "start",
  );

  return (
    <div className={cn("rich-msg-input", disabled && "is-disabled", className)}>
      {/* Formatting bar — hidden until the "A" toggle in the row below asks for
          it, the way Gmail's chat composer works. Kept out of the way because
          most messages are sent without any formatting at all. */}
      {showFormatting && (
        <div ref={toolbarRef} className="rich-msg-toolbar">
          <ToolbarButton
            label="Bold"
            active={active.bold}
            disabled={disabled}
            onClick={() => runCommand("bold")}
          >
            <Bold size={15} />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={active.italic}
            disabled={disabled}
            onClick={() => runCommand("italic")}
          >
            <Italic size={15} />
          </ToolbarButton>
          <ToolbarButton
            label="Strikethrough"
            active={active.strike}
            disabled={disabled}
            onClick={() => runCommand("strikeThrough")}
          >
            <Strikethrough size={15} />
          </ToolbarButton>

          <span className="rich-msg-divider" aria-hidden="true" />

          <ToolbarButton
            label="Bulleted list"
            active={active.list}
            disabled={disabled}
            onClick={() => runCommand("insertUnorderedList")}
          >
            <List size={15} />
          </ToolbarButton>
          <ToolbarButton
            label="Insert link"
            active={openPopover === "link"}
            disabled={disabled}
            onClick={openLinkPopover}
          >
            <Link2 size={15} />
          </ToolbarButton>

          <span className="rich-msg-divider" aria-hidden="true" />

          <ToolbarButton
            label="Inline code"
            active={active.code}
            disabled={disabled}
            onClick={toggleInlineCode}
          >
            <Code size={15} />
          </ToolbarButton>
          <ToolbarButton
            label="Code block"
            active={active.codeBlock}
            disabled={disabled}
            onClick={toggleCodeBlock}
          >
            <SquareCode size={15} />
          </ToolbarButton>

          {openPopover === "link" &&
            createPortal(
              <div
                ref={popoverRef}
                style={linkStyle}
                data-rich-msg-popover=""
                className={cn(popoverBase, "w-[272px]")}
                role="dialog"
                aria-label="Insert link"
              >
                <label className="rich-msg-popover-label" htmlFor="rich-msg-link-text">
                  Text to display
                </label>
                <input
                  id="rich-msg-link-text"
                  value={linkDraft.text}
                  onChange={(e) => setLinkDraft((draft) => ({ ...draft, text: e.target.value }))}
                  placeholder="Optional"
                  className="rich-msg-popover-input mb-2"
                />
                <label className="rich-msg-popover-label" htmlFor="rich-msg-link-url">
                  Link
                </label>
                <input
                  id="rich-msg-link-url"
                  value={linkDraft.url}
                  /* The popover exists only to take this one value, so it takes
                   focus on open rather than costing an extra click. */
                  autoFocus
                  onChange={(e) => {
                    setLinkDraft((draft) => ({ ...draft, url: e.target.value }));
                    setLinkError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyLink();
                    }
                  }}
                  placeholder="https://example.com"
                  className="rich-msg-popover-input"
                />
                {linkError && <p className="rich-msg-popover-error">{linkError}</p>}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setOpenPopover(null)}
                    className="rich-msg-popover-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyLink}
                    className="rich-msg-popover-btn is-primary"
                  >
                    Apply
                  </button>
                </div>
              </div>,
              popoverContainer ?? document.body,
            )}
        </div>
      )}

      {/* The typing surface and its always-visible actions share one row, so
          the icons sit at the trailing edge of the text rather than on a strip
          of their own. items-end keeps them pinned to the last line as the
          editor grows. */}
      <div className="flex items-end gap-1">
        <div
          ref={editorRef}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel}
          aria-disabled={disabled}
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck
          data-empty={isEmpty}
          data-placeholder={placeholder}
          onInput={commit}
          onKeyUp={refreshState}
          onMouseUp={saveSelection}
          onBlur={saveSelection}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="rich-msg-editor"
          style={{ minHeight, maxHeight }}
        />

        <div ref={actionsRef} className="rich-msg-actions">
          <ToolbarButton
            label={showFormatting ? "Hide formatting options" : "Formatting options"}
            active={showFormatting}
            disabled={disabled}
            onClick={() => {
              // Collapsing closes the link popover with it — it lives in the
              // bar that is going away.
              setShowFormatting((current) => {
                if (current && openPopover === "link") setOpenPopover(null);
                return !current;
              });
            }}
          >
            <Baseline size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Emoji"
            active={openPopover === "emoji"}
            disabled={disabled}
            onClick={() => {
              saveSelection();
              setOpenPopover((current) => (current === "emoji" ? null : "emoji"));
            }}
          >
            <Smile size={16} />
          </ToolbarButton>
          {trailingActions}

          {openPopover === "emoji" &&
            createPortal(
              <div
                ref={popoverRef}
                style={emojiStyle}
                data-rich-msg-popover=""
                className={cn(popoverBase, "w-[288px]")}
                role="dialog"
                aria-label="Emoji"
              >
                <div className="max-h-[224px] overflow-y-auto pr-1">
                  {EMOJI_GROUPS.map((group) => (
                    <div key={group.label} className="mb-2 last:mb-0">
                      <p className="rich-msg-popover-label">{group.label}</p>
                      <div className="grid grid-cols-8 gap-0.5">
                        {group.emoji.split(" ").map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertEmoji(emoji)}
                            aria-label={`Insert ${emoji}`}
                            className="rich-msg-emoji"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>,
              popoverContainer ?? document.body,
            )}
        </div>
      </div>
    </div>
  );
};

export default RichMessageInput;
